import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { IconButton } from './Button';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

const maxWidthStyles = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  maxWidth = 'md',
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-[#111111]/40 backdrop-blur-xs transition-opacity animate-in fade-in duration-200"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Dialog Box */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        aria-describedby={description ? 'modal-description' : undefined}
        className={`relative w-full bg-white rounded-3xl border border-[#E5E9E9] shadow-xl overflow-hidden transition-all transform animate-in fade-in zoom-in-95 duration-200 z-10 ${maxWidthStyles[maxWidth]}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 sm:p-6 border-b border-[#E5E9E9]">
          <div className="space-y-1 pr-4">
            {title && (
              <h2 id="modal-title" className="text-lg sm:text-xl font-extrabold text-[#111111] leading-tight">
                {title}
              </h2>
            )}
            {description && (
              <p id="modal-description" className="text-xs sm:text-sm text-[#5F6368]">
                {description}
              </p>
            )}
          </div>
          <IconButton icon={<X className="w-5 h-5" />} aria-label="Close modal" onClick={onClose} size="sm" />
        </div>

        {/* Body */}
        <div className="p-5 sm:p-6 max-h-[70vh] overflow-y-auto space-y-4">{children}</div>

        {/* Footer */}
        {footer && <div className="p-4 sm:p-5 bg-[#F7FAFA] border-t border-[#E5E9E9] flex items-center justify-end gap-3">{footer}</div>}
      </div>
    </div>
  );
};
